import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  filteredSnippets: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { search_title: '', use_for: '' },
  setFilterValues: () => Promise.resolve()
};

const SnippetsContext = createContext(initialState);

export const SnippetsProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [isPreloaded, setPreloaded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [filteredSnippets, setFilteredSnippets] = useState([]);
  const client = useRestApiClient();

  const removeFromList = (snippetId) => {
    setSnippets((prev) => {
      const filtered = prev.filter((row) => row.id !== snippetId);
      return [...filtered];
    });
  };

  const loadSnippets = async (force = false) => {
    if (snippets.length > 0 && !force) return;
    if (isLoading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/snippets');
      setSnippets(message);
      setFilteredSnippets(message);
      setPreloaded(true);
    } catch (error) {
      setSnippets([]);
      setFilteredSnippets([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filterSnippets = snippets;
    const { search_title: searchTitle, use_for: useFor } = filterValues;

    // make sure to only returns published records
    filterSnippets =
      filterSnippets.length > 0 &&
      filterSnippets.filter((record) => +record.publish === 1);

    filterSnippets = filterListBy(filterSnippets, 'use_for', useFor);

    if (searchTitle && searchTitle.length >= 3) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterSnippets = filterSnippets.filter(
        ({ description, title }) =>
          (description &&
            description
              .toString()
              .toLowerCase()
              .indexOf(searchTitle.toLowerCase()) > -1) ||
          (title &&
            title
              .toString()
              .toLowerCase()
              .indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredSnippets(filterSnippets);
  }, [filterValues]);

  const values = useMemo(
    () => ({
      isPreloaded,
      isLoading,
      filterValues,
      snippets,
      filteredSnippets,
      removeFromList,
      setFilterValues,
      loadSnippets
    }),
    [isPreloaded, isLoading, filterValues, snippets, filteredSnippets]
  );

  return (
    <SnippetsContext.Provider value={values} {...contextProviderProps}>
      {children}
    </SnippetsContext.Provider>
  );
};

SnippetsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default SnippetsContext;
